import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ChevronLeft, FileText, Loader2 } from "lucide-react";
import { API } from "@/constants";
import Disclaimer from "@/components/shared/Disclaimer";
import Footer from "@/components/shared/Footer";

const CaseSummaryPage = () => {
  const [form, setForm] = useState({
    case_type: 'personal_injury',
    jurisdiction: 'us_federal',
    case_name: '',
    client_name: '',
    case_facts: '',
    current_status: '',
    next_steps: [],
    key_dates: [],
    summary_type: 'client_letter'
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const generate = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/practice/case-summary`, form);
      setResult(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <main className="min-h-screen bg-slate-50" data-testid="case-summary-page">
      <Disclaimer />
      <div className="container-shell py-8 max-w-5xl">
        <div className="mb-6">
          <Link to="/tools" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4">
            <ChevronLeft className="h-4 w-4" /> Back to Tools
          </Link>
          <h1 className="text-3xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>Case Summary Builder</h1>
          <p className="text-muted-foreground">Generate client letters, status reports, and internal memos</p>
        </div>
        
        {!result ? (
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <CardHeader><CardTitle>Case Details</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label>Case Name</Label>
                      <Input placeholder="Smith v. Jones" value={form.case_name} onChange={e => setForm(f => ({...f, case_name: e.target.value}))} />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Client Name</Label>
                      <Input placeholder="John Smith" value={form.client_name} onChange={e => setForm(f => ({...f, client_name: e.target.value}))} />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Summary Type</Label>
                    <Select value={form.summary_type} onValueChange={v => setForm(f => ({...f, summary_type: v}))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="client_letter">Client Letter</SelectItem>
                        <SelectItem value="internal_memo">Internal Memo</SelectItem>
                        <SelectItem value="status_report">Status Report</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Case Facts</Label>
                    <Textarea placeholder="Describe the key facts..." value={form.case_facts} onChange={e => setForm(f => ({...f, case_facts: e.target.value}))} rows={4} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Current Status</Label>
                    <Textarea placeholder="Current status of the case..." value={form.current_status} onChange={e => setForm(f => ({...f, current_status: e.target.value}))} rows={3} />
                  </div>
                </CardContent>
              </Card>
            </div>
            
            <Card className="bg-slate-600 text-white border-0">
              <CardContent className="p-6">
                <Button className="w-full h-14 text-lg gap-2 bg-white text-slate-700 hover:bg-white/90" onClick={generate} disabled={loading || !form.case_name}>
                  {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> Generating...</> : <><FileText className="h-5 w-5" /> Generate Summary</>}
                </Button>
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="space-y-6 animate-fade-in">
            <Card className="bg-slate-600 text-white border-0">
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold">{result.summary_type?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</h2>
                <p className="text-white/80">{result.case_name} • {result.client_name}</p>
                <Button variant="outline" onClick={() => setResult(null)} className="mt-4 text-white border-white/20">New Summary</Button>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="prose prose-sm max-w-none whitespace-pre-wrap">{result.content}</div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
      <Footer />
    </main>
  );
};


export default CaseSummaryPage;
